import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { cn } from '@src/utils/ui';
import { get } from 'lodash';

export const AutoCompleteInput = ({ name, options = [], disabled = false, onChange = () => true, ...props }) => {
    const { control, formState } = useFormContext();
    const error = !!get(formState.errors, name);
    const helperText = get(formState.errors, `${name}.message`);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { value, ref, ...restField } }) => (
                <AutoCompleteInputField
                    {...restField}
                    {...props}
                    ref={ref}
                    value={value}
                    error={error}
                    helperText={helperText}
                    disabled={disabled}
                    options={options}
                    onChange={(event, selectedValue) => {
                        restField.onChange(selectedValue?.value || '');
                        onChange({ target: { value: selectedValue || '' } });
                    }}
                />
            )}
        />
    );
};

export const AutoCompleteInputField = React.forwardRef(
    (
        { name, by, label, options, className, InputProps = {}, error, helperText, value, containerClass, ...props },
        ref
    ) => {
        const [open, setOpen] = useState(false); // Open state

        const selectedOption = options.find(option =>
            by ? get(option.value, by) === get(value, by) : option.value === value
        );

        const handleToggleOpen = () => {
            setOpen(prev => !prev);
        };

        return (
            <Autocomplete
                {...props}
                name={name}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                className={containerClass}
                options={options}
                getOptionLabel={option => option.label || ''}
                isOptionEqualToValue={(option, val) =>
                    by ? get(option.value, by) === get(val, by) : option.value === val
                }
                value={selectedOption || null}
                componentsProps={{
                    popper: {
                        sx: {
                            '& .MuiAutocomplete-option': {
                                fontSize: '16px'
                            }
                        }
                    }
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        inputRef={ref}
                        name={name}
                        label={label}
                        variant='outlined'
                        error={error}
                        helperText={helperText}
                        className={cn('tw-col-span-2 sm:tw-col-span-1', className)}
                        InputProps={{
                            ...params.InputProps,
                            ...InputProps,
                            className: cn(
                                'tw-text-[16px]',
                                params.disabled ? 'tw-bg-[#0050C40A]' : '',
                                params.InputProps.className,
                                InputProps.className
                            ),
                            sx: { pr: '0px' },
                            endAdornment: (
                                <ExpandMoreIcon
                                    onClick={handleToggleOpen}
                                    sx={{
                                        color: '#3F72AF',
                                        fontSize: 24,
                                        position: 'absolute',
                                        right: '12px',
                                        cursor: 'pointer'
                                    }}
                                />
                            )
                        }}
                        InputLabelProps={{ className: 'tw-text-[16px]' }}
                        FormHelperTextProps={{ className: 'tw-text-[12px]' }}
                        sx={{
                            '& .MuiInputBase-root.Mui-disabled': {
                                backgroundColor: 'rgba(0, 80, 196, 0.04)'
                            },
                            '& .MuiOutlinedInput-root': {
                                pr: '0px'
                            }
                        }}
                    />
                )}
            />
        );
    }
);

AutoCompleteInputField.displayName = 'AutoCompleteInputField';

// -------- PropTypes -------- //

AutoCompleteInput.propTypes = {
    by: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    options: PropTypes.array,
    disabled: PropTypes.bool,
    InputProps: PropTypes.object,
    className: PropTypes.string,
    onChange: PropTypes.func,
    renderOption: PropTypes.func
};

AutoCompleteInputField.propTypes = AutoCompleteInput.propTypes;
